import React, { useState, useEffect } from "react";
import { Popover, Box, Typography, MenuItem, TextField, FormControlLabel, Checkbox, FormGroup, Radio, RadioGroup } from "@mui/material";
import { TrackNowButton } from "../ui";
import { FilterMatchMode } from "primereact/api";
import { tokens } from "../../theme/tokens";

export default function ClientTableFilter({
  anchorEl,
  onClose,
  onApply,
  onClear,
  clients,
  categoriesLookup,
  programList,
  stateList,
  tracknowlicenseTypes,
  postawardlicenseTypes,
  roleType,
  userRoleMode = false,
  roleList = [],
  customerList = [],
  filingMasterMode = false,
  postAwardMode = false,
  postAwardFilingMasterMode = false,
  postAwardDeferredMode = false,
  trackNowDeferredMode = false,
  trackerMode = false,
  requiredOnlyIs = false,
  statusLookup = [],
  jurisdictionLookup = [],
  awardAgenciesList = [],
  awardTypeList = [],
  postAwardPocList = [],
  singleSelectForNonContainsColumns = [],
  singleSelectColumnsByMatchMode = {},
  filterOptionsByColumn = {},
  hideTrackerStaffAssignedFilter = false,
  availableValuesByColumn = {},
  hasActiveFilters = false
}) {
  const [selectedColumn, setSelectedColumn] = useState("");
  const [filterType, setFilterType] = useState("");
  const [textValue, setTextValue] = useState("");
  const [selectedValues, setSelectedValues] = useState([]);
  const [availableOptions, setAvailableOptions] = useState([]);


  const isJurisdiction =
    selectedColumn === "juristiction" ||
    selectedColumn === "filingJuristiction";


  // Define filterable columns based on mode
  const getFilterableColumns = () => {
    if (trackerMode) {
      const trackerColumns = [
        { value: "filingName", label: "Filing Name", type: "text" },
        { value: "dueDateFiling", label: "Filing Due Date", type: "date" },
        { value: "filingFrequency", label: "Filing Frequency", type: "radio" },
        { value: "stateList", label: "State", type: "multiselect" },
        { value: "ruleInfo", label: "Rule Info", type: "text" },
        { value: "filingJuristiction", label: "Jurisdiction", type: "radio" },
        { value: "filingRequired", label: "Required", type: "multiselect" },
        { value: "status", label: "Status", type: "multiselect" },
        { value: "federalCategories", label: "Who Must File Federal", type: "multiselect" },
        { value: "stateCategories", label: "Who Must File in State", type: "multiselect" },
        { value: "awardTypeName", label: "Award Type", type: "multiselect" },
        { value: "awardingAgencyName", label: "Award Agency", type: "multiselect" },
        { value: "staffAssignedArray", label: "Staff Assigned", type: "text" },
        { value: "whereToFile", label: "Where to File", type: "text" }
      ];

      return hideTrackerStaffAssignedFilter
        ? trackerColumns.filter((column) => column.value !== "staffAssignedArray")
        : trackerColumns;
    }

    if (postAwardFilingMasterMode) {
      return [
        { value: "filingName", label: "Filing Name", type: "text" },
        { value: "filingFrequency", label: "Filing Frequency", type: "text" },
        { value: "stateList", label: "State", type: "multiselect" },
        { value: "ruleInfo", label: "Rule Info", type: "text" },
        { value: "required", label: "Required", type: "multiselect" },
        { value: "federalCategories", label: "Who Must File Federal", type: "multiselect" },
        { value: "stateCategories", label: "Who Must File in State", type: "multiselect" },
        { value: "jsicontactName", label: "JSI Staff", type: "text" },
        { value: "juristiction", label: "Jurisdiction", type: "radio" },
        { value: "whereToFile", label: "Where to File", type: "text" }
      ];
    }

    if (postAwardDeferredMode) {
      return [
        { value: "filingName", label: "Filing Name", type: "text" },
        { value: "filingFrequency", label: "Filing Frequency", type: "text" },
        { value: "stateList", label: "State", type: "multiselect" },
        { value: "ruleInfo", label: "Rule Info", type: "text" },
        { value: "federalCategories", label: "Who Must File Federal", type: "multiselect" },
        { value: "stateCategories", label: "Who Must File in State", type: "multiselect" },
        { value: "juristiction", label: "Jurisdiction", type: "radio" },
        { value: "required", label: "Required", type: "multiselect" },
        { value: "jsicontactName", label: "PostAward POC", type: "text" },
        { value: "awardTypeName", label: "Award Type", type: "multiselect" },
        { value: "awardingAgencyName", label: "Award Agency", type: "multiselect" }
      ];
    }

    if (trackNowDeferredMode) {
      return [
        { value: "filingName", label: "Filing Name", type: "text" },
        { value: "filingFrequency", label: "Filing Frequency", type: "radio" },
        { value: "stateList", label: "State", type: "multiselect" },
        { value: "ruleInfo", label: "Rule Info", type: "text" },
        { value: "required", label: "Required", type: "multiselect" },
        { value: "federalCategories", label: "Who Must File Federal", type: "multiselect" },
        { value: "stateCategories", label: "Who Must File in State", type: "multiselect" },
        { value: "jsicontactName", label: "JSI Staff", type: "text" },
        { value: "juristiction", label: "Jurisdiction", type: "radio" }
      ];
    }

    if (userRoleMode) {
      return [
        { value: "userName", label: "User Name", type: "text" },
        { value: "loginId", label: "Login Email", type: "text" },
        { value: "clientName", label: "Client Name", type: "text" },
        { value: "roleName", label: "Role", type: "text" },
        { value: "notificationMail", label: "Include Notification?", type: "radio" }
      ];
    }

    if (filingMasterMode) {
      return [
        { value: "filingName", label: "Filing Name", type: "text" },
        { value: "approverName", label: "Approver Name", type: "text" },
        { value: "filingFrequency", label: "Filing Frequency", type: "text" },
        { value: "stateList", label: "State", type: "multiselect" },
        { value: "ruleInfo", label: "Rule Info", type: "text" },
        { value: "required", label: "Required", type: "multiselect" },
        { value: "federalCategories", label: "Who Must File Federal", type: "multiselect" },
        { value: "stateCategories", label: "Who Must File in State", type: "multiselect" },
        { value: "jsicontactName", label: "JSI Staff", type: "text" },
        { value: "juristiction", label: "Jurisdiction", type: "radio" },
        { value: "workflowStatus", label: "Status", type: "text" }
      ];
    }

    if (postAwardMode) {
      return [
        { value: "filingName", label: "Filing Name", type: "text" },
        { value: "filingFrequency", label: "Filing Frequency", type: "text" },
        { value: "awardType", label: "Award Type", type: "multiselect" },
        { value: "awardingAgency", label: "Award Agency", type: "multiselect" },
        { value: "stateList", label: "State", type: "multiselect" },
        { value: "ruleInfo", label: "Rule Info", type: "text" },
        { value: "required", label: "Required", type: "multiselect" },
        { value: "federalCategories", label: "Who Must File Federal", type: "text" },
        { value: "stateCategories", label: "Who Must File in State", type: "text" },
        { value: "jsicontactName", label: "JSI Staff", type: "text" },
        { value: "juristiction", label: "Jurisdiction", type: "radio" },
        { value: "workflowStatus", label: "Status", type: "text" }
      ];
    }

    const columns = [
      { value: "customerName", label: "Client Name", type: "text" },
      { value: "categories", label: "Federal Business Category", type: "multiselect" },
      { value: "programLists", label: "Program Names", type: "multiselect" },
      { value: "address", label: "Address", type: "text" },
      { value: "stateList", label: "States", type: "multiselect" },
      { value: "taxNumber", label: "Tax Number", type: "text" },
      { value: "phone", label: "Phone", type: "text" }
    ];

    // Add subscription columns based on role
    if (roleType?.includes("JSIPOC") || roleType?.includes("Admin") || roleType?.includes("JSISME")) {
      columns.push({ value: "trackNowLicenseName", label: "TrackNow Subscription", type: "multiselect" });
    }
    if (roleType?.includes("JSIPOC-PostAward") || roleType?.includes("Admin-PostAward") || roleType?.includes("JSISME-PostAward")) {
      columns.push({ value: "postAwardLicenseName", label: "PostAward Subscription", type: "multiselect" });
    }

    return columns;
  };

  const getFilterOptions = () => {
    const column = getFilterableColumns().find(col => col.value === selectedColumn);
    if (!column) return [];

    if (Array.isArray(filterOptionsByColumn?.[selectedColumn])) {
      return filterOptionsByColumn[selectedColumn];
    }
    if (selectedColumn === "stateList") {
      return [
        { value: FilterMatchMode.IN, label: "Contains" },
        { value: FilterMatchMode.NOT_CONTAINS, label: "Is Not" }
      ];
    }
    if (trackNowDeferredMode && selectedColumn === "filingFrequency") {
      return [
        { value: FilterMatchMode.EQUALS, label: "Is" },
        { value: FilterMatchMode.NOT_CONTAINS, label: "Is Not" }
      ];
    }

    if (trackNowDeferredMode && selectedColumn === "juristiction") {
      return [{ value: FilterMatchMode.EQUALS, label: "Is" }];
    }

    if (userRoleMode && selectedColumn === "notificationMail") {
      return [{ value: FilterMatchMode.EQUALS, label: "Is" }];
    }

    if (
      requiredOnlyIs &&
      (selectedColumn === "required" || selectedColumn === "filingRequired")
    ) {
      return [{ value: FilterMatchMode.EQUALS, label: "Is" }];
    }

    if (column.type === "date") {
      return [
        { value: "dateIs", label: "Date Is" },
        { value: "dateIsNot", label: "Date Is Not" },
        { value: "dateBefore", label: "Date Before" },
        { value: "dateAfter", label: "Date After" }
      ];
    } else if (column.type === "radio") {
      return [
        { value: FilterMatchMode.CONTAINS, label: "Contains" },
        { value: FilterMatchMode.EQUALS, label: "Is" },
        { value: FilterMatchMode.NOT_CONTAINS, label: "Is Not" }
      ];
    } else if (column.type === "text") {
      return [
        { value: FilterMatchMode.CONTAINS, label: "Contains" },
        { value: FilterMatchMode.NOT_CONTAINS, label: "Is Not" },
        { value: FilterMatchMode.EQUALS, label: "Is" }
      ];
    } else {
      return [
        { value: FilterMatchMode.IN, label: "Contains" },
        { value: FilterMatchMode.NOT_CONTAINS, label: "Is Not" },
        { value: FilterMatchMode.EQUALS, label: "Is" }
      ];
    }
  };

  const getMatchModeLabel = (matchMode) => {
    const labels = {
      [FilterMatchMode.CONTAINS]: "contains",
      [FilterMatchMode.IN]: "contains",
      [FilterMatchMode.NOT_CONTAINS]: "is not",
      [FilterMatchMode.EQUALS]: "is"
    };
    return labels[matchMode] || matchMode;
  };

  const getAvailableValues = () => {
    if (!selectedColumn) return [];

    if (Array.isArray(availableValuesByColumn?.[selectedColumn])) {
      return availableValuesByColumn[selectedColumn];
    }

    const normalizeOptionList = (input) => {
      if (Array.isArray(input)) {
        return input.filter((item) => item !== null && item !== undefined && item !== "");
      }
      if (typeof input === "string") {
        return input
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean);
      }
      return [];
    };

    const uniqueSortedValues = (values) =>
      Array.from(new Set((values || []).filter(Boolean))).sort((a, b) =>
        String(a).toLowerCase().localeCompare(String(b).toLowerCase())
      );

    if (userRoleMode) {
      switch (selectedColumn) {
        case "clientName":
          return customerList.map(item => item.name);
        case "roleName":
          return roleList.map(item => item.name);
        case "notificationMail":
          return ["True", "False"];
        default:
          return [];
      }
    }

    const toNameList = (list, key) =>
      (list || [])
        .map((item) => (typeof item === "string" ? item : item?.[key]))
        .filter(Boolean);

    if (filingMasterMode || postAwardMode || postAwardFilingMasterMode || postAwardDeferredMode || trackNowDeferredMode || trackerMode) {
      switch (selectedColumn) {
        case "stateList":
          return stateList;
        case "filingFrequency":
          return ["Annually", "Semi-Annually", "Quarterly", "Monthly", "Weekly", "Daily", "Conditional", "Biennial"];
        case "required":
        case "filingRequired":
          return ["True", "False"];
        case "juristiction":
        case "filingJuristiction":
          if (trackNowDeferredMode) {
            return ["Federal", "State", "Both"];
          }
          return jurisdictionLookup.length > 0 ? jurisdictionLookup : ["Federal", "State", "Both"];
        case "status":
          return statusLookup.length > 0 ? statusLookup : ["Pending", "In Progress", "Approved", "Completed"];
        case "awardType":
        case "awardTypeName":
          return awardTypeList.length > 0
            ? toNameList(awardTypeList, "awardTypeName")
            : ["Direct Federal Award", "Direct State Award", "Pass Through Award"];
        case "awardingAgency":
        case "awardingAgencyName":
          return awardAgenciesList.length > 0
            ? toNameList(awardAgenciesList, "awardingAgencyName")
            : ["NTIA", "USDA RUS", "Treasury"];
        case "isDeferred":
          return ["Yes", "No"];
        case "jsicontactName":
          if (postAwardDeferredMode) {
            const pocFromRows = (clients || []).map((item) => item?.jsicontactName).filter(Boolean);
            return Array.from(new Set([...(postAwardPocList || []), ...pocFromRows])).sort((a, b) =>
              a.toLowerCase().localeCompare(b.toLowerCase())
            );
          }
          return [];
        case "federalCategories":
          return programList && programList.length > 0
            ? toNameList(programList, "programName")
            : normalizeOptionList(categoriesLookup);
        case "stateCategories":
          return programList && programList.length > 0
            ? toNameList(programList, "programName")
            : normalizeOptionList(categoriesLookup);
        default:
          return [];
      }
    }

    switch (selectedColumn) {
      case "categories":
        return categoriesLookup.map(item => item.businessCategoryName);
      case "programLists":
        return programList.map(item => item.programName);
      case "stateList":
        return stateList;
      case "trackNowLicenseName":
        return tracknowlicenseTypes;
      case "postAwardLicenseName":
        return postawardlicenseTypes;
      default:
        return [];
    }
  };

  useEffect(() => {
    setAvailableOptions(getAvailableValues());
    setSelectedValues([]);
    setTextValue("");
    setFilterType("");
  }, [selectedColumn]);

  const handleColumnChange = (value) => {
    setSelectedColumn(value);
  };

  const handleFilterTypeChange = (value) => {
    setFilterType(value);
  };

  const isSingleSelectForNonContains = (columnName) =>
    singleSelectForNonContainsColumns.includes(columnName) &&
    filterType !== FilterMatchMode.IN;

  const isSingleSelectForMatchMode = (columnName) =>
    Array.isArray(singleSelectColumnsByMatchMode?.[columnName]) &&
    singleSelectColumnsByMatchMode[columnName].includes(filterType);

  const handleValueChange = (value) => {
    const columnType = getFilterableColumns().find(col => col.value === selectedColumn)?.type;
    const isSingleSelectCurrentColumn =
      isSingleSelectForNonContains(selectedColumn) ||
      isSingleSelectForMatchMode(selectedColumn);

    if (columnType === "multiselect") {
      if (isSingleSelectCurrentColumn) {
        setSelectedValues([value]);
        return;
      }

      if (
        requiredOnlyIs &&
        (selectedColumn === "required" || selectedColumn === "filingRequired")
      ) {
        setSelectedValues([value]);
        return;
      }

      if (trackerMode && selectedColumn === "status") {
        setSelectedValues([value]);
        return;
      }

      const currentValues = selectedValues || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      setSelectedValues(newValues);
    } else if (columnType === "radio") {
      setSelectedValues([value]);
    } else {
      setTextValue(value);
    }
  };

  const getOptionValue = (option) => {
    if (option && typeof option === "object") {
      if (Object.prototype.hasOwnProperty.call(option, "value")) return option.value;
      if (Object.prototype.hasOwnProperty.call(option, "name")) return option.name;
      if (Object.prototype.hasOwnProperty.call(option, "label")) return option.label;
      if (Object.prototype.hasOwnProperty.call(option, "programName")) return option.programName;
      if (Object.prototype.hasOwnProperty.call(option, "businessCategoryName")) return option.businessCategoryName;
    }
    return option;
  };

  const getOptionLabel = (option) => {
    const normalizedValue = getOptionValue(option);
    if (normalizedValue === true) return "True";
    if (normalizedValue === false) return "False";
    if (normalizedValue === null || normalizedValue === undefined) return "";
    return String(normalizedValue);
  };

  const handleApply = () => {
    if (!selectedColumn || (!filterType && !isJurisdiction)) return;

    const column = getFilterableColumns().find(col => col.value === selectedColumn);
    const isSingleSelectCurrentColumn =
      isSingleSelectForNonContains(selectedColumn) ||
      isSingleSelectForMatchMode(selectedColumn);
    const filterValue =
      column.type === "multiselect"
        ? (isSingleSelectCurrentColumn ? (selectedValues[0] || "") : selectedValues)
        : column.type === "radio"
          ? (selectedValues[0] || "")
          : textValue;

    if ((column.type === "multiselect" && selectedValues.length === 0) ||
      (column.type === "radio" && selectedValues.length === 0) ||
      ((column.type === "text" || column.type === "date") && !String(textValue).trim())) {
      return;
    }

    // Create filter structure matching old PrimeReact format
    const isRequiredField =
      requiredOnlyIs &&
      (selectedColumn === "required" || selectedColumn === "filingRequired");

    const normalizedRequiredValue = (() => {
      if (!isRequiredField) return filterValue;
      const rawValue = Array.isArray(filterValue) ? filterValue[0] : filterValue;
      if (typeof rawValue === "string") {
        if (rawValue.toLowerCase() === "true") return true;
        if (rawValue.toLowerCase() === "false") return false;
      }
      return rawValue;
    })();
    const normalizedMatchMode = isJurisdiction
      ? FilterMatchMode.IN
      : isRequiredField
        ? FilterMatchMode.EQUALS
        : filterType;


    const filterConfig = {
      field: selectedColumn,
      matchMode: normalizedMatchMode,
      value: normalizedRequiredValue,
      matchModeLabel: getMatchModeLabel(normalizedMatchMode)
    };

    onApply(filterConfig);

    // Reset form fields
    setSelectedColumn("");
    setFilterType("");
    setTextValue("");
    setSelectedValues([]);
    setAvailableOptions([]);

    onClose();
  };


  const handleClear = () => {
    setSelectedColumn("");
    setFilterType("");
    setTextValue("");
    setSelectedValues([]);
    setAvailableOptions([]);
    onClear();
    onClose();
  };
  //console.log("selectedColumn", selectedColumn);

  const renderValueInput = () => {
    const column = getFilterableColumns().find(col => col.value === selectedColumn);
    if (!column) return null;
    const isRequiredSingleSelect =
      requiredOnlyIs &&
      (selectedColumn === "required" || selectedColumn === "filingRequired");

    if (column.type === "date") {
      return (
        <TextField
          fullWidth
          label="Date"
          type="date"
          size="small"
          sx={{
            mt: 2,
            '& .MuiOutlinedInput-root': {
              backgroundColor: tokens.colors.offWhite,
              '& fieldset': {
                borderColor: '#ccc',
              },
              '&:hover fieldset': {
                borderColor: tokens.colors.connectedPurple,
              },
            },
          }}
          InputLabelProps={{ shrink: true }}
          value={textValue}
          onChange={(e) => setTextValue(e.target.value)}
        />
      );
    } else if (column.type === "text") {
      return (
        <TextField
          fullWidth
          label="Value"
          size="small"
          sx={{
            mt: 2,
            '& .MuiOutlinedInput-root': {
              backgroundColor: tokens.colors.offWhite,
              '& fieldset': {
                borderColor: '#ccc',
              },
              '&:hover fieldset': {
                borderColor: tokens.colors.connectedPurple,
              },
            },
          }}
          value={textValue}
          onChange={(e) => setTextValue(e.target.value)}
        />
      );
    } else if (column.type === "radio" || isRequiredSingleSelect) {
      return (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>Select Value:</Typography>
          <Box sx={{
            maxHeight: 200,
            overflowY: 'auto',
            border: '1px solid #ccc',
            borderRadius: 1,
            p: 1,
            backgroundColor: tokens.colors.offWhite
          }}>
            <RadioGroup
              value={(selectedValues || [])[0] || ""}
              onChange={(e) => handleValueChange(e.target.value)}
            >
              {availableOptions.map((option) => {
                const optionValue = getOptionValue(option);
                const optionLabel = getOptionLabel(option);
                return (
                  <FormControlLabel
                    key={`${selectedColumn}-${String(optionValue)}`}
                    value={optionValue}
                    control={<Radio size="small" />}
                    label={optionLabel}
                    sx={{
                      fontSize: '14px',
                      '& .MuiFormControlLabel-label': {
                        fontSize: '14px'
                      },
                      mb: 0.5
                    }}
                  />
                )
              })}
            </RadioGroup>
          </Box>
        </Box>
      );
    } else {
      const isTrackerStatusFilter = trackerMode && selectedColumn === "status";
      const isSingleSelectCurrentColumn =
        isSingleSelectForNonContains(selectedColumn) ||
        isSingleSelectForMatchMode(selectedColumn);
      if (isTrackerStatusFilter || isSingleSelectCurrentColumn) {
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {isTrackerStatusFilter ? "Select Status:" : "Select Value:"}
            </Typography>
            <Box sx={{
              maxHeight: 200,
              overflowY: 'auto',
              border: '1px solid #ccc',
              borderRadius: 1,
              p: 1,
              backgroundColor: tokens.colors.offWhite
            }}>
              <RadioGroup
                value={(selectedValues || [])[0] || ""}
                onChange={(e) => handleValueChange(e.target.value)}
              >
                {availableOptions.map((option) => {
                  const optionValue = getOptionValue(option);
                  const optionLabel = getOptionLabel(option);
                  return (
                    <FormControlLabel
                      key={`${selectedColumn}-${String(optionValue)}`}
                      value={optionValue}
                      control={<Radio size="small" />}
                      label={optionLabel}
                      sx={{
                        fontSize: '14px',
                        '& .MuiFormControlLabel-label': {
                          fontSize: '14px'
                        },
                        mb: 0.5
                      }}
                    />
                  )
                })}
              </RadioGroup>
            </Box>
          </Box>
        );
      }

      return (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>Select Values:</Typography>
          <Box sx={{
            maxHeight: 200,
            overflowY: 'auto',
            border: '1px solid #ccc',
            borderRadius: 1,
            p: 1,
            backgroundColor: tokens.colors.offWhite
          }}>
            <FormGroup>
              {availableOptions.map((option) => {
                const optionValue = getOptionValue(option);
                const optionLabel = getOptionLabel(option);
                return (
                  <FormControlLabel
                    key={`${selectedColumn}-${String(optionValue)}`}
                    control={
                      <Checkbox
                        checked={(selectedValues || []).includes(optionValue)}
                        onChange={() => handleValueChange(optionValue)}
                        size="small"
                      />
                    }
                    label={optionLabel}
                    sx={{
                      fontSize: '14px',
                      '& .MuiFormControlLabel-label': {
                        fontSize: '14px'
                      },
                      mb: 0.5
                    }}
                  />
                )
              })}
            </FormGroup>
          </Box>
        </Box>
      );
    }
  };

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      PaperProps={{
        sx: {
          backgroundColor: tokens.colors.offWhite,
          border: '1px solid #ccc'
        }
      }}
    >
      <Box sx={{ p: 2, width: 350, maxHeight: 500, overflowY: 'auto' }}>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>Filter</Typography>

        <TextField
          select
          fullWidth
          label="Column"
          size="small"
          value={selectedColumn}
          onChange={(e) => handleColumnChange(e.target.value)}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: tokens.colors.offWhite,
              '& fieldset': {
                borderColor: '#ccc',
              },
              '&:hover fieldset': {
                borderColor: tokens.colors.connectedPurple,
              },
            },
          }}
        >
          {getFilterableColumns().map((column) => (
            <MenuItem key={column.value} value={column.value}>
              {column.label}
            </MenuItem>
          ))}
        </TextField>

        {selectedColumn &&
          selectedColumn !== "juristiction" &&
          selectedColumn !== "filingJuristiction" && (
            <TextField
              select
              fullWidth
              label="Condition"
              size="small"
              sx={{
                mt: 2,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: tokens.colors.offWhite,
                  '& fieldset': {
                    borderColor: '#ccc',
                  },
                  '&:hover fieldset': {
                    borderColor: tokens.colors.connectedPurple,
                  },
                },
              }}
              value={filterType}
              onChange={(e) => handleFilterTypeChange(e.target.value)}
            >
              {getFilterOptions().map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        {/* 3rd field value input should show if there is a selected column and either a filter type is selected or the column is jurisdiction (which only has one filter type) */}
        {selectedColumn && (filterType || selectedColumn === "juristiction" || selectedColumn === "filingJuristiction") && renderValueInput()}

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
          <TrackNowButton
            size="small"
            variant="secondary"
            onClick={handleClear}
            disabled={!hasActiveFilters && !selectedColumn && !textValue && (!selectedValues || selectedValues.length === 0)}
          >
            Clear
          </TrackNowButton>
          <TrackNowButton size="small" variant="primary" onClick={handleApply}>
            Apply Filter
          </TrackNowButton>
        </Box>
      </Box>
    </Popover>
  );
}
